/**
 * BreadcrumbLabelContext — cross-page registry that maps route
 * segments (typically UUIDs / opaque IDs) to human-friendly labels
 * for the sticky breadcrumb strip.
 *
 * How it works
 * ------------
 * Any page that renders an entity by ID (e.g. `/app-catalog/my-apps/17CA…`,
 * `/dashboards/8F2D…`) calls the `useBreadcrumbLabel(id, name)` hook once
 * the entity is loaded. That writes `{[id]: name}` into a Map held in
 * this provider. `Breadcrumbs.jsx` consults this Map before falling back
 * to the "truncate UUID" auto-labeller.
 *
 * Design notes
 * ------------
 *   • In-memory only — cleared on hard reload; that's fine because
 *     the label is re-registered as soon as the page loads.
 *   • Keyed by the raw segment (usually an ID) not the full path so
 *     the same entity name is reused wherever it appears in the URL.
 *   • No hardcoded types / entity shapes — the hook is generic.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const BreadcrumbLabelCtx = createContext({
    labels:   {},
    setLabel: () => {},
});

export function BreadcrumbLabelProvider({ children }) {
    const [labels, setLabels] = useState({});
    const setLabel = useCallback((key, label) => {
        if (!key || !label) return;
        setLabels((prev) => (prev[key] === label ? prev : { ...prev, [key]: label }));
    }, []);
    return (
        <BreadcrumbLabelCtx.Provider value={{ labels, setLabel }}>
            {children}
        </BreadcrumbLabelCtx.Provider>
    );
}

export function useBreadcrumbLabels() {
    return useContext(BreadcrumbLabelCtx).labels;
}

/**
 * Register a friendly label for a route segment (usually the entity id).
 * Safe to call before `id` / `name` are known — it becomes a no-op
 * until both are truthy.
 */
export function useBreadcrumbLabel(id, name) {
    const { setLabel } = useContext(BreadcrumbLabelCtx);
    useEffect(() => {
        if (id && name) setLabel(id, name);
    }, [id, name, setLabel]);
}
